import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  JOY_JOB_STATUS_LABELS,
  JOY_MAX_MESSAGE_CHARS,
  type JoyJobsView,
  type JoyThreadView,
} from '@goyone/shared';
import { api } from '../lib/api';
import { dateTime, num, relative, time } from '../lib/format';
import { Icon } from './Icon';
import { Logo } from './Logo';
import { useToast } from './Toast';
import { MessageMenu, copyText, buildReplyQuote } from './MessageMenu';

/**
 * جوي في الفقاعة.
 *
 * ## الانتظارُ بالسحب
 *
 * الرسالةُ تُرسل ويعود الطلب فورًا، وجوي يعمل في الخادم. والمحادثةُ تُسأل
 * كلَّ ثانيتين ما دام يعمل، وكلَّ ربع دقيقةٍ بعدها — لتظهر نتائجُ المهامّ
 * والتنبيهات حين تُنشر لا حين يُعاد فتحُ الفقاعة.
 */

const RUNNING_MS = 2000;
const IDLE_MS = 15_000;

/** أسئلةٌ تُعرّف المديرَ بما يستطيعه جوي — تُرسل بنقرة. */
const SUGGESTIONS = [
  'إيه أخبار الشركة النهارده؟',
  'مين عنده متابعات متأخرة؟',
  'مين ما سجّلش حضوره لحد دلوقتي؟',
  'اعملي تقرير الإيراد الشهري آخر ٦ شهور',
  'كل يوم الساعة ٩ الصبح ابعتلي ملخص المتأخرات',
  'نبّهني لو موظف فضل ساعة من غير نشاط وهو حاضر',
];

/* ==========================================================================
   العلامة
   ========================================================================== */

/** علامةُ جوي: شرارةٌ على لون الشركة — مساعدٌ منها لا تطبيقٌ غريب. */
export function JoyMark({ size = 34 }: { size?: number }) {
  return (
    <span className="joy-mark" style={{ width: size, height: size }} aria-hidden="true">
      <svg viewBox="0 0 24 24" width={size * 0.56} height={size * 0.56}>
        <path
          d="M12 2.5c.5 3.9 1.9 6.9 4.2 8.2 1.3.7 2.9 1.1 4.8 1.3-1.9.2-3.5.6-4.8 1.3-2.3 1.3-3.7 4.3-4.2 8.2-.5-3.9-1.9-6.9-4.2-8.2C6.5 12.6 4.9 12.2 3 12c1.9-.2 3.5-.6 4.8-1.3C10.1 9.4 11.5 6.4 12 2.5Z"
          fill="currentColor"
        />
        <circle cx="19" cy="5" r="1.6" fill="currentColor" opacity="0.8" />
      </svg>
    </span>
  );
}

/* ==========================================================================
   الاختيار: جوي أم الفريق
   ========================================================================== */

export function DockHome({
  teamUnread,
  onJoy,
  onTeam,
  onClose,
}: {
  teamUnread: number;
  onJoy: () => void;
  onTeam: () => void;
  onClose: () => void;
}) {
  const thread = useQuery({
    queryKey: ['joy', 'thread'],
    queryFn: () => api<JoyThreadView>('/joy/thread'),
    refetchInterval: IDLE_MS,
  });
  const running = thread.data?.status === 'RUNNING';

  return (
    <>
      <header className="dock-head">
        <span className="dock-head-brand">
          <Logo size={22} mono />
        </span>
        <div className="dock-head-text">
          <strong>GoyOne</strong>
          <span className="tiny">مع من تريد أن تتحدّث؟</span>
        </div>
        <button className="dock-icon-btn" onClick={onClose} title="إغلاق" aria-label="إغلاق">
          <Icon name="x" size={17} />
        </button>
      </header>

      <div className="dock-body dock-home">
        <button className="dock-choice is-joy" onClick={onJoy}>
          <JoyMark size={48} />
          <span className="dock-choice-text">
            <strong>تحدّث مع جوي</strong>
            <span>
              مساعدُك الذكيّ: اسأله عن الشركة، واطلب تقارير، وكلّفه بمهامّ في مواعيدها ومتابعة
              العمل.
            </span>
            {running && <span className="dock-choice-status">يعمل على ردّك الآن…</span>}
          </span>
          <Icon name="chevronLeft" size={16} />
        </button>

        <button className="dock-choice" onClick={onTeam}>
          <span className="dock-group-avatar is-brand" style={{ width: 48, height: 48 }}>
            <Icon name="users" size={22} />
          </span>
          <span className="dock-choice-text">
            <strong>تحدّث مع الفريق</strong>
            <span>رسائلُ مباشرة ومجموعاتٌ مع زملائك داخل الشركة.</span>
          </span>
          {teamUnread > 0 ? (
            <span className="dock-unread num">{teamUnread > 99 ? '99+' : num(teamUnread)}</span>
          ) : (
            <Icon name="chevronLeft" size={16} />
          )}
        </button>
      </div>
    </>
  );
}

/* ==========================================================================
   محادثة جوي
   ========================================================================== */

export function JoyView({
  onBack,
  onJobs,
  onClose,
}: {
  onBack: () => void;
  onJobs: () => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const stream = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);

  const thread = useQuery({
    queryKey: ['joy', 'thread'],
    queryFn: () => api<JoyThreadView>('/joy/thread'),
    refetchInterval: (q) => (q.state.data?.status === 'RUNNING' ? RUNNING_MS : IDLE_MS),
  });

  const data = thread.data;
  const running = data?.status === 'RUNNING';
  const messages = data?.messages ?? [];
  const lastId = messages.length ? messages[messages.length - 1]!.id : null;

  useEffect(() => {
    const el = stream.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastId, running]);

  useEffect(() => {
    input.current?.focus();
  }, []);

  const send = useMutation({
    mutationFn: (text: string) =>
      api<JoyThreadView>('/joy/messages', { method: 'POST', body: { text } }),
    onSuccess: (next) => {
      setDraft('');
      queryClient.setQueryData(['joy', 'thread'], next);
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإرسال.'),
  });

  const reset = useMutation({
    mutationFn: () => api<JoyThreadView>('/joy/reset', { method: 'POST' }),
    onSuccess: (next) => queryClient.setQueryData(['joy', 'thread'], next),
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر بدء محادثة جديدة.'),
  });

  const submit = (text = draft) => {
    const body = text.trim();
    if (!body || running || send.isPending) return;
    send.mutate(body);
  };

  const disabled = data?.configured === false;

  return (
    <>
      <header className="dock-head is-joy">
        <button className="dock-icon-btn" onClick={onBack} title="رجوع" aria-label="رجوع">
          <Icon name="chevronRight" size={20} />
        </button>
        <JoyMark size={34} />
        <div className="dock-head-text">
          <strong>جوي</strong>
          <span className="tiny">
            {running ? 'يفكّر ويجمع البيانات…' : 'مساعدُك الذكيّ في GoyOne'}
          </span>
        </div>
        <button
          className="dock-icon-btn"
          onClick={onJobs}
          title="المهامّ والمراقبة"
          aria-label="المهامّ والمراقبة"
        >
          <Icon name="clock" size={17} />
        </button>
        <button
          className="dock-icon-btn"
          onClick={() => reset.mutate()}
          disabled={running || reset.isPending || messages.length === 0}
          title="محادثة جديدة"
          aria-label="محادثة جديدة"
        >
          <Icon name="refresh" size={16} />
        </button>
        <button className="dock-icon-btn" onClick={onClose} title="إغلاق" aria-label="إغلاق">
          <Icon name="x" size={17} />
        </button>
      </header>

      <div className="dock-stream joy-stream" ref={stream}>
        {thread.isPending && <div className="skeleton" style={{ height: 80 }} />}
        {thread.isError && <p className="dock-empty">تعذّر فتح محادثة جوي.</p>}

        {disabled && (
          <div className="joy-setup">
            <strong>جوي غير مفعّل بعد</strong>
            <p>
              يحتاج جوي مفتاحًا من Anthropic على الخادم. أضف <code>ANTHROPIC_API_KEY</code> إلى ملف
              الإعدادات <code>.env</code> ثم أعد تشغيل الخادم.
            </p>
            <p className="tiny">
              المهامُّ وقواعدُ المراقبة المحفوظة تبقى، والقواعدُ تعمل بلا مفتاح لأنّها تُحسب في
              النظام.
            </p>
          </div>
        )}

        {data && messages.length === 0 && !disabled && (
          <div className="joy-intro">
            <JoyMark size={56} />
            <strong>أهلًا، أنا جوي</strong>
            <p>
              أقرأ بيانات شركتك وأجيبك بأرقامها، وأُعدّ التقارير، وأنفّذ ما تطلبه في مواعيده،
              وأنبّهك حين يختلّ شيءٌ في العمل.
            </p>
            <div className="joy-chips">
              {SUGGESTIONS.map((s) => (
                <button key={s} className="joy-chip" onClick={() => submit(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) =>
          m.kind === 'NOTICE' ? (
            <div key={m.id} className="joy-notice" title={dateTime(m.createdAt)}>
              <JoyText text={m.text} />
              <span className="dock-meta">{relative(m.createdAt)}</span>
            </div>
          ) : (
            <div key={m.id} className="dock-msg-wrap">
              <div
                className={`dock-msg joy-msg ${m.kind === 'USER' ? 'is-out' : 'is-in joy-answer'}`}
              >
                {m.kind === 'USER' ? (
                  <span className="dock-text">{m.text}</span>
                ) : (
                  <JoyText text={m.text} />
                )}
                <span className="dock-meta" title={dateTime(m.createdAt)}>
                  {time(m.createdAt)}
                </span>
                {/* خيارُ الرسالةِ في جوي: نسخٌ وردٌّ — بلا تحويلٍ ولا حذف. */}
                <MessageMenu
                  align={m.kind === 'USER' ? 'end' : 'start'}
                  actions={[
                    {
                      key: 'copy',
                      label: 'نسخ',
                      icon: 'copy',
                      onClick: () =>
                        void copyText(m.text).then((ok) =>
                          toast(ok ? 'نُسخت الرسالة' : 'تعذّر النسخ'),
                        ),
                    },
                    {
                      key: 'reply',
                      label: 'ردّ',
                      icon: 'reply',
                      onClick: () => {
                        setDraft(buildReplyQuote(m.kind === 'USER' ? 'أنت' : 'جوي', m.text));
                        input.current?.focus();
                      },
                    },
                  ]}
                />
              </div>
            </div>
          ),
        )}

        {running && (
          <div className="dock-msg-wrap">
            <div className="dock-msg is-in joy-typing" aria-label="جوي يكتب">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}

        {data?.status === 'FAILED' && data.lastError && (
          <div className="joy-error">{data.lastError}</div>
        )}
      </div>

      <div className="dock-composer">
        <textarea
          ref={input}
          className="dock-input"
          rows={1}
          value={draft}
          disabled={disabled}
          onChange={(e) => {
            setDraft(e.target.value.slice(0, JOY_MAX_MESSAGE_CHARS));
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={
            disabled ? 'جوي غير مفعّل' : running ? 'انتظر ردّ جوي…' : 'اسأل جوي أو اطلب منه'
          }
          aria-label="رسالة إلى جوي"
        />
        <button
          className="dock-send"
          onClick={() => submit()}
          disabled={disabled || running || send.isPending || !draft.trim()}
          title="إرسال"
          aria-label="إرسال"
        >
          <Icon name="send" size={18} />
        </button>
      </div>
    </>
  );
}

/* ==========================================================================
   المهامّ والمراقبة
   ========================================================================== */

export function JoyJobsView({ onBack, onClose }: { onBack: () => void; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const jobs = useQuery({
    queryKey: ['joy', 'jobs'],
    queryFn: () => api<JoyJobsView>('/joy/jobs'),
    refetchInterval: 30_000,
  });

  const act = useMutation({
    mutationFn: (a: {
      type: 'tasks' | 'watches';
      id: string;
      action: 'pause' | 'resume' | 'delete';
    }) =>
      a.action === 'delete'
        ? api<JoyJobsView>(`/joy/${a.type}/${a.id}`, { method: 'DELETE' })
        : api<JoyJobsView>(`/joy/${a.type}/${a.id}`, {
            method: 'PATCH',
            body: { status: a.action === 'pause' ? 'PAUSED' : 'ACTIVE' },
          }),
    onSuccess: (next) => queryClient.setQueryData(['joy', 'jobs'], next),
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  /* الحذفُ بنقرتين: مهمّةٌ صباحيّةٌ تُحذف خطأً لا يلاحظ غيابَها أحدٌ أيامًا. */
  const [confirming, setConfirming] = useState<string | null>(null);

  const tasks = jobs.data?.tasks ?? [];
  const watches = jobs.data?.watches ?? [];

  const controls = (type: 'tasks' | 'watches', id: string, status: string) => (
    <span className="joy-job-actions">
      <button
        className="link-btn tiny"
        onClick={() => act.mutate({ type, id, action: status === 'ACTIVE' ? 'pause' : 'resume' })}
        disabled={act.isPending}
      >
        {status === 'ACTIVE' ? 'إيقاف' : 'استئناف'}
      </button>
      <button
        className="link-btn tiny link-danger"
        onClick={() => {
          if (confirming !== id) return setConfirming(id);
          setConfirming(null);
          act.mutate({ type, id, action: 'delete' });
        }}
        onBlur={() => setConfirming((c) => (c === id ? null : c))}
        disabled={act.isPending}
      >
        {confirming === id ? 'تأكيد الحذف' : 'حذف'}
      </button>
    </span>
  );

  return (
    <>
      <header className="dock-head is-joy">
        <button className="dock-icon-btn" onClick={onBack} title="رجوع" aria-label="رجوع">
          <Icon name="chevronRight" size={20} />
        </button>
        <div className="dock-head-text">
          <strong>مهامّ جوي ومراقبته</strong>
          <span className="tiny">
            {num(tasks.length)} مهامّ · {num(watches.length)} قواعد
          </span>
        </div>
        <button className="dock-icon-btn" onClick={onClose} title="إغلاق" aria-label="إغلاق">
          <Icon name="x" size={17} />
        </button>
      </header>

      <div className="dock-body joy-jobs">
        {jobs.isPending && <div className="skeleton" style={{ height: 80, margin: 12 }} />}

        {jobs.data && tasks.length === 0 && watches.length === 0 && (
          <div className="dock-empty">
            <p>لا مهامّ ولا قواعد بعد.</p>
            <p className="tiny">
              اطلبها من جوي بكلامك: «كل يوم الساعة ٩ ابعتلي ملخّص المتأخرات»، أو «نبّهني لو حد ما
              سجّلش حضوره قبل ١٠».
            </p>
          </div>
        )}

        {tasks.length > 0 && <p className="dock-section">المهامّ المجدولة</p>}
        {tasks.map((t) => (
          <div
            key={t.id}
            className="joy-job"
            style={t.status === 'PAUSED' ? { opacity: 0.55 } : undefined}
          >
            <div className="joy-job-top">
              <strong>{t.title}</strong>
              <span className="tiny mute">{JOY_JOB_STATUS_LABELS[t.status]}</span>
            </div>
            <span className="tiny">
              {t.scheduleText}
              {t.alertOnly ? ' · للتنبيه فقط' : ''}
            </span>
            <span className="tiny mute joy-job-prompt">{t.prompt}</span>
            <span className="tiny mute">
              {t.status === 'ACTIVE' && t.nextRunAt ? `التالي ${dateTime(t.nextRunAt)}` : ''}
              {t.lastRunAt
                ? ` · آخر تشغيل ${relative(t.lastRunAt)}${t.lastOk === false ? ' (تعذّر)' : ''}`
                : ''}
            </span>
            {controls('tasks', t.id, t.status)}
          </div>
        ))}

        {watches.length > 0 && <p className="dock-section">قواعد المراقبة</p>}
        {watches.map((w) => (
          <div
            key={w.id}
            className="joy-job"
            style={w.status === 'PAUSED' ? { opacity: 0.55 } : undefined}
          >
            <div className="joy-job-top">
              <strong>{w.label}</strong>
              <span className="tiny mute">{JOY_JOB_STATUS_LABELS[w.status]}</span>
            </div>
            <span className="tiny">{w.text}</span>
            <span className="tiny mute">
              {w.notifyEmployee ? 'يُنبَّه الموظّف المعنيّ أيضًا · ' : ''}
              {w.lastTriggeredAt ? `آخر تنبيه ${relative(w.lastTriggeredAt)}` : 'لم تجد خللًا بعد'}
            </span>
            {controls('watches', w.id, w.status)}
          </div>
        ))}
      </div>
    </>
  );
}

/* ==========================================================================
   نصُّ جوي
   ========================================================================== */

/**
 * ماركداون جوي — ما يكتبه النموذجُ فعلًا وحدَه.
 *
 * فقراتٌ ونقاطٌ وجداولُ وخطٌّ عريض. عناصرُ React لا HTML يُحقن: نصُّ النموذج
 * يحمل بياناتٍ كتبها موظّفون — اسمُ عميلٍ فيه وسمٌ لا يصير وسمًا.
 */
export function JoyText({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;
    const trimmed = line.trim();

    if (!trimmed) {
      i++;
      continue;
    }

    if (trimmed.startsWith('|')) {
      const rows: string[][] = [];
      while (i < lines.length && lines[i]!.trim().startsWith('|')) {
        const cells = lines[i]!.trim()
          .replace(/^\||\|$/g, '')
          .split('|')
          .map((c) => c.trim());
        if (!cells.every((c) => /^:?-{2,}:?$/.test(c))) rows.push(cells);
        i++;
      }
      const [head, ...body] = rows;
      blocks.push(
        <div key={blocks.length} className="joy-table-wrap">
          <table className="joy-table">
            {head && (
              <thead>
                <tr>
                  {head.map((c, k) => (
                    <th key={k}>{inline(c)}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {body.map((r, k) => (
                <tr key={k}>
                  {r.map((c, j) => (
                    <td key={j}>{inline(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const bullet = /^([-*•]|\d+[.)])\s+/;
    if (bullet.test(trimmed)) {
      const ordered = /^\d/.test(trimmed);
      const items: string[] = [];
      while (i < lines.length && bullet.test(lines[i]!.trim())) {
        items.push(lines[i]!.trim().replace(bullet, ''));
        i++;
      }
      const List = ordered ? 'ol' : 'ul';
      blocks.push(
        <List key={blocks.length} className="joy-list">
          {items.map((it, k) => (
            <li key={k}>{inline(it)}</li>
          ))}
        </List>,
      );
      continue;
    }

    if (/^#{1,6}\s/.test(trimmed)) {
      blocks.push(
        <p key={blocks.length} className="joy-heading">
          {inline(trimmed.replace(/^#{1,6}\s+/, ''))}
        </p>,
      );
      i++;
      continue;
    }

    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() &&
      !lines[i]!.trim().startsWith('|') &&
      !bullet.test(lines[i]!.trim()) &&
      !/^#{1,6}\s/.test(lines[i]!.trim())
    ) {
      para.push(lines[i]!.trim());
      i++;
    }
    blocks.push(
      <p key={blocks.length} className="joy-para">
        {para.map((p, k) => (
          <Fragment key={k}>
            {k > 0 && <br />}
            {inline(p)}
          </Fragment>
        ))}
      </p>,
    );
  }

  return <div className="joy-text">{blocks}</div>;
}

/** الخطُّ العريض والشيفرة داخل السطر. */
function inline(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
  return parts.map((part, k) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={k}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return <code key={k}>{part.slice(1, -1)}</code>;
    }
    return <Fragment key={k}>{part}</Fragment>;
  });
}
